/**
 * Central model registry — single source of truth for all AI model metadata.
 *
 * To add a new model:
 *   1. Add an entry to MODEL_REGISTRY below.
 *   2. That's it — the model dropdown, context-window indicator, and
 *      validation logic all derive from this registry automatically.
 */

export interface ModelSpec {
    /** Display label shown in dropdowns */
    label: string;
    /** Deployment / model id sent to the backend */
    value: string;
    /** Maximum context-window size in tokens */
    contextWindow: number;
    /** Estimated tokens consumed by the system prompt (role, rules, schema) */
    systemPromptTokens: number;
    /** Estimated tokens consumed by tool definitions injected into the context */
    toolDefinitionTokens: number;
    /** Whether this model is the default for new agents */
    isDefault?: boolean;
}

export const MODEL_REGISTRY: ModelSpec[] = [
    {
        label: 'GPT-4o Mini',
        value: 'gpt-4o-mini',
        contextWindow: 128_000,
        systemPromptTokens: 3_000,
        toolDefinitionTokens: 20_700,
        isDefault: true,
    },
    {
        label: 'GPT-5.1 Codex Mini',
        value: 'gpt-5.1-codex-mini',
        contextWindow: 1_047_576,
        systemPromptTokens: 3_000,
        toolDefinitionTokens: 20_700,
    },
];

/** Quick lookup by model value */
export const getModelSpec = (modelValue: string): ModelSpec =>
    MODEL_REGISTRY.find((m) => m.value === modelValue) ?? MODEL_REGISTRY.find((m) => m.isDefault)!;

/** Default model */
export const DEFAULT_MODEL = MODEL_REGISTRY.find((m) => m.isDefault)!;

/** All valid model values (used for migration/validation) */
export const VALID_MODEL_VALUES = MODEL_REGISTRY.map((m) => m.value);

/** Options array for dropdowns (label + value) */
export const MODEL_OPTIONS = MODEL_REGISTRY.map(({ label, value }) => ({ label, value }));
